import React, { useEffect, useState, useRef, useCallback } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { useMessages } from "../../contexts/MessageContext.jsx";
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import "../../styles/ChatContent.css";
import "../../styles/Workspaces.css";
import LeftPanel from "./LeftPanel.tsx";
import ChatPanel from "./ChatPanel.tsx";
import NewChatCompose from "./NewChatCompose.tsx";
import PowerAppEmbed from "./PowerAppEmbed.tsx";
import store from '../../utils/store.ts';
import { createChat, createInstance, getAllUsers, getAllAgents, getInformation, getNegotiate, getUser } from "../../resources/api.resource.ts";
import { usePersistentChatGroups } from "../../hooks/usePersistentChatGroups";
import { EMPTY_INSTANCE_TOPIC, findReusableNewChatInstance } from "../../utils/newChatInstance";

export interface ConversationDraft {
  html: string;
  files: File[];
}
import { UserMapValue } from "../../models/User.ts";
import { transformUser, transformAgent, buildUsersMap } from '../../utils/userTransformers';
import { MessageItem, CollaborationInstance } from "../../models/LeftPanel";
import CanvasPanel from "./CanvasPanel";
import { useCanvasPanel } from "../../contexts/CanvasPanelContext";
import { CanvasCommand, PubSubCommand } from "../../constants/pubsubCommands";

// Logo and customization will be loaded from public folder at runtime
// This allows them to be added/modified after build without requiring rebuild
interface WorkspaceItem {
  id?: string;
  name?: string;
  projectType?: string;
  collaborationId?: string;
  powerAppId?: string;
  powerAppTenantId?: string;
  members?: Array<{ id: string; name?: string }>;
  chatType?: "group" | "direct" | "project";
  [key: string]: any;
}

interface WorkspacesProps {
  fetchedMessages?: any;
  initialCollaborationId?: string;
  showLeftPanel?: boolean;
  allUsersMap?: Record<string, UserMapValue>;
  collaborations?: any[];
  logoUrl?: string | null;
  currentUser?: any;
  // When true (login / fresh Chat view), auto-selecting the assistant DM opens
  // a brand-new chat instead of the first existing instance. Reuses an empty
  // "New chat" at the top if one is already there.
  newChatFlag?: boolean;
}

const buildLeftPanelData = (
  collabs: any[] = [],
  usersMap: Record<string, UserMapValue> = {},
  currentUserId?: string
) => {
  const currentUid = currentUserId?.toLowerCase();
  const groups = collabs.filter((c: any) => c.collaborationType === "group");
  const projects = collabs.filter((c: any) => c.collaborationType === "project");
  const dms = collabs.filter((c: any) => c.collaborationType === "dm");

  return [
    {
      name: "Groups",
      items: groups.map((g: any) => ({
        collaborationId: g.collab_instances?.find((i: any) => i.status === 'active')?.id ?? g.collaborationId,
        collaborationIdSummary: g.collaborationId,
        name: g.name || "Unnamed Group",
        iconName: "GroupsIcon",
        notificationCount: (g.collab_instances || []).reduce((sum: number, inst: any) => sum + (inst.status === 'active' ? (inst.unreadCount || 0) : 0), 0),
        memberUids: g.memberUids,
        collab_instances: g.collab_instances,
      })),
    },
    {
      name: "Projects",
      items: projects.map((p: any) => ({
        collaborationId: p.collab_instances?.find((i: any) => i.status === 'active')?.id ?? p.collaborationId,
        collaborationIdSummary: p.collaborationId,
        name: p.name || "Unnamed Project",
        iconName: "FeedIcon",
        notificationCount: (p.collab_instances || []).reduce((sum: number, inst: any) => sum + (inst.status === 'active' ? (inst.unreadCount || 0) : 0), 0),
        memberUids: p.memberUids,
        collab_instances: p.collab_instances,
      })),
    },
    {
      name: "Direct Messages",
      items: dms.map((dm: any) => {
        const otherUid = dm.memberUids?.find(
          (uid: string) => uid.toLowerCase() !== currentUid
        );
        const displayName =
          usersMap[otherUid?.toLowerCase()]?.name || "Unknown User";

        return {
          collaborationId: dm.collab_instances?.find((i: any) => i.status === 'active')?.id ?? dm.collaborationId,
          collaborationIdSummary: dm.collaborationId,
          name: displayName,
          iconName: "PersonIcon",
          notificationCount: (dm.collab_instances || []).reduce((sum: number, inst: any) => sum + (inst.status === 'active' ? (inst.unreadCount || 0) : 0), 0),
          memberUids: dm.memberUids,
          collab_instances: dm.collab_instances,
        };
      }),
    },
  ];
};

const findFirstAgentDmItem = (
  panelData: any[],
  usersMap: Record<string, UserMapValue>,
  currentUserId?: string
) => {
  const currentUid = currentUserId?.toLowerCase();
  const dmSection = panelData.find((s: any) => s.name === "Direct Messages");
  if (!dmSection) return null;

  return dmSection.items.find((item: any) => {
    const otherUid = item.memberUids?.find(
      (uid: string) => uid.toLowerCase() !== currentUid
    );
    return otherUid && usersMap[otherUid.toLowerCase()]?.userType === 'agent';
  }) || null;
};

export default function Workspaces({
  fetchedMessages,
  initialCollaborationId,
  showLeftPanel = true,
  allUsersMap: propsAllUsersMap,
  collaborations: propsCollaborations,
  logoUrl: propsLogoUrl,
  currentUser: propsCurrentUser,
  newChatFlag = false,
}: WorkspacesProps) {
  const { messages } = useMessages();
  const queryClient = useQueryClient();

  const [selectedItem, setSelectedItem] = useState<WorkspaceItem | null>(null);
  const [isComposingDM, setIsComposingDM] = useState(false);
  const [isComposingGroup, setIsComposingGroup] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [selectedInstance, setSelectedInstanceState] = useState<CollaborationInstance | null>(null);
  const setSelectedInstance = (val: CollaborationInstance | null) => {
    selectedInstanceRef.current = val;
    setSelectedInstanceState(val);
  };
  const [leftPanelData, setLeftPanelData] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allUsersMap, setAllUsersMap] = useState<Record<string, UserMapValue>>(
    propsAllUsersMap || {}
  );
  const [collaborations, setCollaborations] = useState<any[]>(
    propsCollaborations || []
  );
  const [tenantId, setTenantId] = useState<string>("");
  const [logoUrl, setLogoUrl] = useState<string | null>(propsLogoUrl || null);
  const [isLoadingCollaborations, setIsLoadingCollaborations] = useState(true);
  const [collaborationError, setCollaborationError] = useState<string | null>(null);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const hasAutoSelected = useRef(false);
  const hasAutoSelectedAgent = useRef(false);
  // Fires once per mount (not reset on tab switches) so the "open a new chat on
  // login" behaviour only happens on the initial landing, not every time the
  // user returns to the Chat tab.
  const hasCreatedInitialNewChat = useRef(false);
  const draftsRef = useRef<Map<string, ConversationDraft>>(new Map());
  const selectedInstanceRef = useRef<CollaborationInstance | null>(null);
  const leftPanelDataRef = useRef<any[]>([]);
  const [draftGroupIds, setDraftGroupIds] = useState<Set<string>>(new Set());

  const handleDraftChange = useCallback((groupId: string, draft: ConversationDraft) => {
    if (draft.html.trim() || draft.files.length > 0) {
      draftsRef.current.set(groupId, draft);
      setDraftGroupIds(prev => {
        if (prev.has(groupId)) return prev;
        const next = new Set(prev);
        next.add(groupId);
        return next;
      });
    } else {
      draftsRef.current.delete(groupId);
      setDraftGroupIds(prev => {
        if (!prev.has(groupId)) return prev;
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
    }
  }, []);

  const canvas = useCanvasPanel();

  const handleCanvasCommand = useCallback((cmd: CanvasCommand) => {
    switch (cmd.type) {
      case PubSubCommand.CanvasShow:
        canvas.show();
        break;
      case PubSubCommand.CanvasHide:
        canvas.hide();
        break;
      case PubSubCommand.CanvasLoadDocument:
        canvas.loadDocument(cmd.document);
        break;
    }
  }, [canvas]);

  const { subscribeToGroup } = usePersistentChatGroups({
    socket,
    tenantId,
    queryClient,
    uId: currentUser?.userId || '',
    activeGroupId: selectedItem?.collaborationId,
    onCanvasCommand: handleCanvasCommand,
    onBackgroundMessage: useCallback((groupId: string, _summaryId: string) => {
      setLeftPanelData(prev => prev.map(section => ({
        ...section,
        items: section.items.map((item: any) => {
          const isInstanceMatch = (item.collab_instances || []).some(
            (inst: any) => inst.id === groupId
          );
          if (!isInstanceMatch) return item;

          const updatedInstances = (item.collab_instances || []).map((inst: any) =>
            inst.id === groupId
              ? { ...inst, unreadCount: (inst.unreadCount || 0) + 1 }
              : inst
          );
          const totalUnread = updatedInstances.reduce((sum: number, inst: any) => sum + (inst.status === 'active' ? (inst.unreadCount || 0) : 0), 0);

          return { ...item, collab_instances: updatedInstances, notificationCount: totalUnread, unread: true };
        }),
      })));
    }, []),
  });

  // Filter leftPanelData for chat view: only show DMs with agents
  const filteredLeftPanelData = React.useMemo(() => {
    if (showLeftPanel) return leftPanelData;
    // Only show the DM section with agent DMs, hide Groups and Projects
    const dmSection = leftPanelData.find((section: any) => section.name === "Direct Messages");
    if (!dmSection) return [];
    const currentUid = currentUser?.userId?.toLowerCase();
    // Filter to only show DMs where the other member is an agent
    const agentDMs = dmSection.items.filter((item: any) => {
      const otherUid = item.memberUids?.find(
        (uid: string) => uid.toLowerCase() !== currentUid
      );
      return otherUid && allUsersMap[otherUid.toLowerCase()]?.userType === 'agent';
    });
    // Always return the Direct Messages section, even if empty, so users can start new DMs
    return [{ name: "Direct Messages", items: agentDMs }];
  }, [leftPanelData, showLeftPanel, currentUser, allUsersMap]);

  // Sync props to state when they change
  useEffect(() => {
    if (propsAllUsersMap) {
      setAllUsersMap(propsAllUsersMap);
    }
  }, [propsAllUsersMap]);

  useEffect(() => {
    if (propsCollaborations) {
      setCollaborations(propsCollaborations);
    }
  }, [propsCollaborations]);

  useEffect(() => {
    if (propsLogoUrl !== undefined) {
      setLogoUrl(propsLogoUrl);
    }
  }, [propsLogoUrl]);

  // Reset auto-selection when switching navbar options
  useEffect(() => {
    if (!showLeftPanel) {
      // Switching to Chat view - allow agent auto-selection again
      hasAutoSelectedAgent.current = false;
      setSelectedItem(null); // Clear current selection
    } else {
      // Switching to Collaborations view - re-select the same conversation if one was selected
      if (selectedItem && selectedItem.collaborationId) {
        const currentCollabId = selectedItem.collaborationId;

        // Find the item in leftPanelData and simulate a click
        for (const section of leftPanelData) {
          const item = section.items.find((i: any) => i.collaborationId === currentCollabId);
          if (item) {
            handleSelectItem(item, section.name);
            return;
          }
        }
      }
      // If no item was selected or not found, clear selection
      setSelectedItem(null);
    }
  }, [showLeftPanel]);

  const fetchCollaborations = useCallback(async () => {
    try {
      const collabs = await getInformation();
      setCollaborations(collabs);
      store.dispatch({
        type: 'set',
        collaborations: collabs,
      });
      setLeftPanelData(buildLeftPanelData(collabs, allUsersMap, currentUser?.userId));
    } catch (err: any) {
      console.error("Failed to fetch collaborations:", err);
    }
  }, [currentUser, allUsersMap]);

  // Keep ref in sync so closure-bound handlers (ws.onmessage) can read current leftPanelData
  useEffect(() => {
    leftPanelDataRef.current = leftPanelData;
  }, [leftPanelData]);

  // Pure helpers for optimistic panel updates
  const findItemBySummaryId = (data: any[], summaryId: string): any | null => {
    for (const section of data) {
      const found = section.items.find(
        (item: any) => item.collaborationIdSummary === summaryId || item.collaborationId === summaryId
      );
      if (found) return found;
    }
    return null;
  };

  const insertItemIntoSection = (data: any[], sectionName: string, item: any): any[] =>
    data.map((section) => {
      if (section.name !== sectionName) return section;
      const alreadyExists = section.items.some(
        (i: any) => i.collaborationIdSummary === item.collaborationIdSummary
      );
      if (alreadyExists) return section;
      return { ...section, items: [item, ...section.items] };
    });

  const showToast = (message: string) => {
    const toast = document.createElement("div");
    toast.setAttribute("role", "status");
    toast.style.position = "fixed";
    toast.style.bottom = "20px";
    toast.style.left = "50%";
    toast.style.transform = "translateX(-50%)";
    // #27ae60 gave white text only 2.87:1. #1B7A43 is 5.37:1 (WCAG 1.4.3 AA).
    toast.style.background = "#1B7A43";
    toast.style.color = "white";
    toast.style.padding = "10px 20px";
    toast.style.borderRadius = "5px";
    toast.style.zIndex = "1000";
    toast.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
    document.body.appendChild(toast);
    // The element has to be in the DOM and empty *before* it gets text.
    // Appending a node that already has its text reads to assistive tech as a
    // new subtree rather than a change to a region it is watching, so invites,
    // renames and mentions were announced to nobody.
    setTimeout(() => { toast.textContent = message; }, 0);
    setTimeout(() => toast.remove(), 3000);
  };


 const hasInitialized = useRef(false);


 useEffect(() => {
  if (hasInitialized.current) return;
  hasInitialized.current = true;

  const initData = async () => {
    setIsLoadingCollaborations(true);
    try {
      const user = propsCurrentUser ?? await getUser();
      setCurrentUser(user);

      // Set tenantId from user membership
      if (user?.membership?.tenantId) {
        setTenantId(user.membership.tenantId.toLowerCase());
      }

      let resolvedUsersMap = propsAllUsersMap || allUsersMap;
      let resolvedCollaborations = propsCollaborations || collaborations;

      // Only fetch users/agents/collaborations if NOT provided via props
      if (!propsAllUsersMap || !propsCollaborations) {
        // Fetch both users and agents in parallel
        const [rawUsers, rawAgents] = await Promise.all([
          getAllUsers(),
          getAllAgents().catch(err => {
            console.warn('Failed to fetch agents, continuing with users only:', err);
            return [];
          })
        ]);

        // Transform to internal format
        const usersWithType = rawUsers.map(transformUser);
        const agentsWithType = rawAgents.map(transformAgent);
        const allUsers = [...usersWithType, ...agentsWithType];

        // Create map for lookups
        const map = buildUsersMap(allUsers);
        resolvedUsersMap = map;
        setAllUsersMap(map);

        const collabs = await getInformation();
        resolvedCollaborations = collabs;
        setCollaborations(collabs);

        // Dispatch to Redux only if we fetched it
        store.dispatch({
          type: 'set',
          collaborations: collabs,
          allUsers: allUsers,
          user: user
        });
      } else {
        // Props were provided, just dispatch user to Redux
        store.dispatch({
          type: 'set',
          user: user
        });
      }

      setLeftPanelData(buildLeftPanelData(resolvedCollaborations, resolvedUsersMap, user?.userId));

      const negotiate = await getNegotiate();
      const ws = new WebSocket(negotiate.url, "json.webpubsub.azure.v1");
      setSocket(ws);

      ws.onopen = () => {
        setTimeout(() => {
          // Join user's personal group
          ws.send(
            JSON.stringify({
              type: "joinGroup",
              group: `${user.membership.tenantId.toLowerCase()}_user_${user.userId.toLowerCase()}`
            })
          );

          ws.send(
            JSON.stringify({
              type: "sendToGroup",
              group: `${user.membership.tenantId.toLowerCase()}_user_${user.userId.toLowerCase()}`,
              dataType: "json",
              data: JSON.stringify({
                type: "system",
                message: `${user.name} connected to CollaborationHub`
              })
            })
          );
        }, 5000);
      };

  ws.onmessage = async (event) => {
  const msg = JSON.parse(event.data);
  if (msg.type === "message" && msg.from === "group" && msg.dataType === "json") {
    const rawInner = JSON.parse(msg.data);
    const inner = rawInner.type === "system" && rawInner.event ? { ...rawInner, type: rawInner.event } : rawInner;
    switch (inner.type) {
      case "collaboration_invite": {
        const incomingId = inner.payload.collaborationId;
        const alreadyInPanel = incomingId
          && findItemBySummaryId(leftPanelDataRef.current, incomingId);
        if (!alreadyInPanel) {
          showToast(`You've been invited by ${inner.payload.actor} to '${inner.payload.name}'`);
          await fetchCollaborations();
        }
        if (incomingId) {
          queryClient.invalidateQueries({
            queryKey: ['conversationMessages', incomingId],
          });
        }
        break;
      }

      case "collaboration_removed":
                   showToast(
                     `${inner.payload.actor} removed '${inner.payload.name}' (${inner.payload.type})`
                   );

        await fetchCollaborations();

        // Remove cache completely
        if (inner.payload.collaborationId) {
          queryClient.removeQueries({
            queryKey: ['conversationMessages', inner.payload.collaborationId],
          });
        }

        if (selectedItem?.collaborationId === inner.payload.collaborationId) {
          setSelectedItem(null);
        }
        break;

      case "chat_message":
        // Suppress toast for streaming messages — the streaming bubble appears inline
        if (inner?.status !== 'streaming') {
          showToast(`${inner.authorName}: ${inner.content}`);
        }
        // Refresh collaborations to update unreadCount
        await fetchCollaborations();
        // Cache invalidation happens automatically when unreadCount > 0
        break;

      case "mention": {
        const payload = inner.payload;
        setLeftPanelData(prevData =>
          prevData.map((section: any) => ({
            ...section,
            items: section.items.map((item: any) =>
              item.collaborationId === payload.collaborationId ||
              item.collaborationIdSummary === payload.collaborationId ||
              (item.collab_instances || []).some((inst: any) => inst.id === payload.collaborationId)
                ? { ...item, hasMention: true }
                : item
            )
          }))
        );
        break;
      }

      case "topic_updated":
        // Update instance topic via WebSocket notification
        if (inner.collabInstanceId && inner.topic) {
          setLeftPanelData(prevData => {
            return prevData.map(section => {
              return {
                ...section,
                items: section.items.map((item: any) => {
                  // Check if this item contains the instance that was updated
                  const hasInstance = (item.collab_instances || []).some(
                    (inst: CollaborationInstance) => inst.id === inner.collabInstanceId
                  );

                  if (!hasInstance) return item;

                  return {
                    ...item,
                    collab_instances: (item.collab_instances || []).map((inst: CollaborationInstance) =>
                      inst.id === inner.collabInstanceId ? { ...inst, topic: inner.topic } : inst
                    )
                  };
                })
              };
            });
          });

          // Update selected instance if it matches the updated one
          const currentSelected = selectedInstanceRef.current;
          if (currentSelected?.id === inner.collabInstanceId) {
            setSelectedInstance({ ...currentSelected, topic: inner.topic } as CollaborationInstance);
          }

          showToast(`Chat renamed to "${inner.topic}"`);
        }
        break;

      case "instance_deleted":
        // Remove instance via WebSocket notification
        if (inner.collabInstanceId) {
          const wasSelected = selectedInstance?.id === inner.collabInstanceId;
          let nextInstanceToSelect: { instance: CollaborationInstance; parentItem: any; sectionName: string } | null = null;

          // Find the parent item and remaining instances before updating state
          if (wasSelected) {
            for (const section of leftPanelData) {
              for (const item of section.items) {
                const hasInstance = (item.collab_instances || []).some(
                  (inst: CollaborationInstance) => inst.id === inner.collabInstanceId
                );

                if (hasInstance) {
                  const remainingInstances = (item.collab_instances || []).filter(
                    (inst: CollaborationInstance) => inst.id !== inner.collabInstanceId && inst.status === 'active'
                  );

                  if (remainingInstances.length > 0) {
                    nextInstanceToSelect = {
                      instance: remainingInstances[0],
                      parentItem: item,
                      sectionName: section.name
                    };
                  }
                  break;
                }
              }
              if (nextInstanceToSelect) break;
            }
          }

          setLeftPanelData(prevData => {
            return prevData.map(section => {
              return {
                ...section,
                items: section.items.map((item: any) => {
                  // Check if this item contains the instance that was deleted
                  const hasInstance = (item.collab_instances || []).some(
                    (inst: CollaborationInstance) => inst.id === inner.collabInstanceId
                  );

                  if (!hasInstance) return item;

                  return {
                    ...item,
                    collab_instances: (item.collab_instances || []).filter(
                      (inst: CollaborationInstance) => inst.id !== inner.collabInstanceId
                    )
                  };
                })
              };
            });
          });

          // Auto-select next instance or clear selection
          if (wasSelected) {
            if (nextInstanceToSelect) {
              const { instance, parentItem, sectionName } = nextInstanceToSelect;
              const chatType = sectionName === "Groups" ? "group" : sectionName === "Projects" ? "project" : "direct";

              setSelectedInstance(instance);
              setSelectedItem({
                ...parentItem,
                collaborationId: instance.id,
                name: instance.topic || parentItem.name,
                chatType
              });
            } else {
              setSelectedInstance(null);
              setSelectedItem(null);
            }
          }

          showToast('Chat deleted successfully');
        }
        break;

      case "instance_created":
        if (inner.collabInstanceId && inner.collaborationId && inner.topic) {

          // Instance created by another user — add it to the list and notify
          const newInstance: CollaborationInstance = {
            id: inner.collabInstanceId,
            topic: inner.topic,
            status: 'active'
          };

          setLeftPanelData(prevData => {
            return prevData.map(section => ({
              ...section,
              items: section.items.map((item: any) => {
                const itemSummaryId = item.collaborationIdSummary || item.collaborationId;
                if (itemSummaryId !== inner.collaborationId) return item;
                const alreadyExists = (item.collab_instances || []).some(
                  (inst: CollaborationInstance) => inst.id === newInstance.id
                );
                if (alreadyExists) return item;
                return {
                  ...item,
                  collab_instances: [newInstance, ...(item.collab_instances || [])]
                };
              })
            }));
          });

          showToast(`New chat "${inner.topic}" created`);
        }
        break;

      case "unread_count_updated": {
        const { collaborationId: instanceId, unreadCount } = inner.payload;
        setLeftPanelData(prevData =>
          prevData.map(section => ({
            ...section,
            items: section.items.map((item: any) => {
              const updatedInstances = (item.collab_instances || []).map((inst: any) =>
                inst.id === instanceId ? { ...inst, unreadCount } : inst
              );
              const totalUnread = updatedInstances.reduce(
                (sum: number, inst: any) => sum + (inst.status === 'active' ? (inst.unreadCount || 0) : 0), 0
              );
              return {
                ...item,
                collab_instances: updatedInstances,
                notificationCount: totalUnread,
                unread: totalUnread > 0,
              };
            }),
          }))
        );
        break;
      }

      default:
        break;
    }
  }
};


      ws.onerror = (err) => console.error("WebSocket error:", err);
      ws.onclose = () => console.warn("WebSocket closed");
    } catch (err: any) {
      console.error("Initialization error:", err);
      setError(err.message);
    } finally {
      setIsLoadingCollaborations(false);
    }
  };

  initData();
}, []);

  const handleSelectItem = (item: MessageItem, sectionName: string) => {
    setIsComposingDM(false);
    let chatType: WorkspaceItem["chatType"] = "direct";
    if (sectionName === "Groups") chatType = "group";
    else if (sectionName === "Projects") chatType = "project";
    setSelectedItem({ ...item, chatType } as any);
    if (item.hasMention) {
      setLeftPanelData(prevData =>
        prevData.map((section: any) => ({
          ...section,
          items: section.items.map((i: any) =>
            i.collaborationId === item.collaborationId ||
            i.collaborationIdSummary === item.collaborationId
              ? { ...i, hasMention: false }
              : i
          )
        }))
      );
    }
  };

  // Select an instance (existing or newly created) of an assistant DM as the
  // active chat, mirroring what LeftPanel does when the user picks one.
  const selectInstance = (dmItem: any, instance: CollaborationInstance) => {
    setIsComposingDM(false);
    setSelectedInstance(instance);
    setSelectedItem({
      ...dmItem,
      collaborationId: instance.id,
      name: instance.topic || dmItem.name,
      chatType: "direct",
    } as any);
  };

  // Open a fresh chat on the assistant DM, applying the same rule as the
  // "+ New Chat" button: if the newest instance is still an empty "New chat",
  // reuse it instead of stacking up another one.
  const openNewChatInstance = async (dmItem: any) => {
    const reusable = findReusableNewChatInstance(queryClient, dmItem);
    if (reusable) {
      selectInstance(dmItem, reusable);
      return;
    }

    const collaborationIdSummary = dmItem.collaborationIdSummary || dmItem.collaborationId;
    const response = await createInstance({
      collaborationId: collaborationIdSummary,
      topic: EMPTY_INSTANCE_TOPIC,
    });
    const newInstance: CollaborationInstance = {
      id: response.id || response.instanceId,
      topic: response.topic || EMPTY_INSTANCE_TOPIC,
      status: response.status || "active",
    };

    // Prepend the new instance so LeftPanel can find and highlight it (the
    // WebSocket "instance_created" echo is deduped by id).
    setLeftPanelData(prev => prev.map(section => {
      if (section.name !== "Direct Messages") return section;
      return {
        ...section,
        items: section.items.map((item: any) => {
          const itemSummaryId = item.collaborationIdSummary || item.collaborationId;
          const parentSummaryId = dmItem.collaborationIdSummary || dmItem.collaborationId;
          if (itemSummaryId !== parentSummaryId) return item;
          const alreadyExists = (item.collab_instances || []).some(
            (inst: any) => inst.id === newInstance.id
          );
          if (alreadyExists) return item;
          return { ...item, collab_instances: [newInstance, ...(item.collab_instances || [])] };
        }),
      };
    }));

    selectInstance(dmItem, newInstance);
  };

  // Auto-selection landing point for an assistant DM. On login (newChatFlag)
  // this opens a new chat once; otherwise it selects the DM normally (which
  // cascades to its first instance in LeftPanel).
  const finalizeAgentDmSelection = async (dmItem: any) => {
    if (newChatFlag && !hasCreatedInitialNewChat.current) {
      hasCreatedInitialNewChat.current = true;
      try {
        await openNewChatInstance(dmItem);
        return;
      } catch (err) {
        console.error("Failed to open a new chat on login, selecting the DM instead:", err);
      }
    }
    handleSelectItem(dmItem, "Direct Messages");
  };

  // Combined auto-selection logic: URL parameter OR first agent
  useEffect(() => {
    // Wait until data is loaded
    if (isLoadingCollaborations || !currentUser || Object.keys(allUsersMap).length === 0) {
      return;
    }

    if (initialCollaborationId && collaborations.length === 0) {
      return;
    }

    // Check if we need to auto-select anything
    const shouldAutoSelect =
      (initialCollaborationId && !hasAutoSelected.current) ||
      (!showLeftPanel && !hasAutoSelectedAgent.current);

    if (!shouldAutoSelect) {
      setIsAutoSelecting(false);
      return;
    }

    setIsAutoSelecting(true);

    // PRIORITY 1: URL parameter (existing behavior)
    if (initialCollaborationId && !hasAutoSelected.current) {
      hasAutoSelected.current = true;

      const targetCollab = collaborations.find(
        (c: any) => c.collab_instances.find( (collab: any) => collab.id ===initialCollaborationId)
      );
      if (!targetCollab) {
        setCollaborationError(
          "Collaboration not found or you don't have access to it."
        );
        setSelectedItem(null);
        setIsAutoSelecting(false);
        return;
      }

      // Determine section name
      let sectionName = "Groups";
      if (targetCollab.collaborationType === "project") {
        sectionName = "Projects";
      } else if (targetCollab.collaborationType === "dm") {
        sectionName = "Direct Messages";
      }

      // Find the item in leftPanelData — search by collaborationId, summary ID, or any instance
      const section = leftPanelData.find((s: any) => s.name === sectionName);
      const item = section?.items.find(
        (i: any) =>
          i.collaborationId === initialCollaborationId ||
          i.collaborationIdSummary === initialCollaborationId ||
          (i.collab_instances || []).some((inst: any) => inst.id === initialCollaborationId)
      );

      if (item) {
        handleSelectItem(item, sectionName);
      }
      setIsAutoSelecting(false);
      return; // URL takes priority, stop here
    }

    // PRIORITY 2: First agent DM (when showLeftPanel is false)
    if (!showLeftPanel && !hasAutoSelectedAgent.current) {
      hasAutoSelectedAgent.current = true;

      // Find the Direct Messages section
      const dmSection = leftPanelData.find((s: any) => s.name === "Direct Messages");

      const existingAgentDm = dmSection
        ? findFirstAgentDmItem(leftPanelData, allUsersMap, currentUser?.userId)
        : null;

      if (existingAgentDm) {
        void (async () => {
          try {
            await finalizeAgentDmSelection(existingAgentDm);
          } finally {
            setIsAutoSelecting(false);
          }
        })();
        return;
      }

      const firstAgentEntry = Object.entries(allUsersMap).find(
        ([, userInfo]) => userInfo.userType === 'agent'
      );

      if (!firstAgentEntry) {
        console.log("No agents available for auto-selection");
        setIsAutoSelecting(false);
        return;
      }

      const [agentUid, agentInfo] = firstAgentEntry;

      void (async () => {
        try {
          console.log(`No agent DMs found; creating DM with ${agentInfo.name || agentUid}`);
          const created = await createChat({
            type: "dm",
            name: "DM",
            memberUids: [agentUid],
          });

          const refreshedCollabs = await getInformation();
          setCollaborations(refreshedCollabs);
          store.dispatch({
            type: 'set',
            collaborations: refreshedCollabs,
          });

          const refreshedPanelData = buildLeftPanelData(
            refreshedCollabs,
            allUsersMap,
            currentUser?.userId
          );
          setLeftPanelData(refreshedPanelData);

          const createdIds = new Set(
            [
              created?.activeCollabInstanceId,
              created?.collabInstanceId,
              created?.pubsubGroupId,
              created?.collaborationId,
            ].filter(Boolean)
          );

          const refreshedDmSection = refreshedPanelData.find(
            (s: any) => s.name === "Direct Messages"
          );
          const createdItem = refreshedDmSection?.items.find(
            (item: any) =>
              createdIds.has(item.collaborationId) ||
              createdIds.has(item.collaborationIdSummary) ||
              (item.collab_instances || []).some((inst: any) => createdIds.has(inst.id))
          ) || findFirstAgentDmItem(refreshedPanelData, allUsersMap, currentUser?.userId);

          if (createdItem) {
            await finalizeAgentDmSelection(createdItem);
          } else {
            console.log("Agent DM was created but could not be selected");
          }
        } catch (err) {
          console.error("Failed to create agent DM for auto-selection:", err);
        } finally {
          setIsAutoSelecting(false);
        }
      })();
    }
  }, [
    initialCollaborationId,
    collaborations,
    isLoadingCollaborations,
    leftPanelData,
    showLeftPanel,
    currentUser,
    allUsersMap
  ]);

  const renderRightPanel = () => {
    const handleCreated = ({ item, isExisting, chatType }: { item: any; isExisting: boolean; chatType: "direct" | "group" }) => {
      setIsComposingDM(false);
      setIsComposingGroup(false);
      setIsCreatingChat(false);
      if (isExisting) {
        const existing = findItemBySummaryId(leftPanelData, item.collaborationIdSummary!);
        if (existing) {
          setSelectedItem({ ...existing, chatType } as any);
        } else {
          fetchCollaborations();
        }
        return;
      }
      const sectionName = chatType === "group" ? "Groups" : "Direct Messages";
      setLeftPanelData(prev => insertItemIntoSection(prev, sectionName, item));
      setSelectedItem({ ...item, chatType } as any);
      subscribeToGroup({ groupId: item.collaborationId!, summaryId: item.collaborationIdSummary! });
    };

    if (!selectedItem) {
      return (
        <div style={{ position: 'relative', height: '100%' }}>
          {isComposingDM && (
            <NewChatCompose
              mode="dm"
              allUsersMap={allUsersMap ?? {}}
              currentUser={currentUser}
              onSubmitting={() => setIsCreatingChat(true)}
              onCreated={handleCreated}
              onCancel={() => { setIsComposingDM(false); setIsCreatingChat(false); }}
            />
          )}
          {isComposingGroup && (
            <NewChatCompose
              mode="group"
              allUsersMap={allUsersMap ?? {}}
              currentUser={currentUser}
              onCreated={handleCreated}
              onCancel={() => setIsComposingGroup(false)}
            />
          )}
          <ActiveChatPanel
            selectedItem={selectedItem}
            chatTitle=""
            uId=""
            tenantId={tenantId}
            chatType="direct"
            isLoadingCollaborations={isCreatingChat || isLoadingCollaborations}
            collaborationError={collaborationError}
            subscribeToGroup={subscribeToGroup}
          />
        </div>
      );
    }

    if (selectedItem.projectType === "PowerApp") {
      return (
        <PowerAppEmbed
          appId={selectedItem.powerAppId || ""}
          tenantId={selectedItem.powerAppTenantId || ""}
          title={selectedItem.name || ""}
        />
      );
    }

    return (
      <ActiveChatPanel
        key={selectedItem.collaborationId}
        selectedItem={selectedItem}
        chatTitle={selectedItem.name ?? "Chat"}
        instanceTitle={selectedInstance?.topic}
        groupId={selectedItem.collaborationId || ""}
        uId={currentUser?.userId || ""}
        tenantId={tenantId}
        chatType={selectedItem.chatType || "direct"}
        socket={socket === null ? undefined : socket}
        allUsersMap={allUsersMap}
        onLeaveRoomSuccess={() => {
          setSelectedItem(null);
          fetchCollaborations();
        }}
        onSidebarRefresh={fetchCollaborations}
        onMarkAsRead={(instanceId) => {
          setLeftPanelData(prev => prev.map(section => ({
            ...section,
            items: section.items.map((item: any) => {
              const hasInstance = (item.collab_instances || []).some((inst: any) => inst.id === instanceId);
              if (!hasInstance) return item;
              const updatedInstances = (item.collab_instances || []).map((inst: any) =>
                inst.id === instanceId ? { ...inst, unreadCount: 0 } : inst
              );
              const totalUnread = updatedInstances.reduce((sum: number, inst: any) => sum + (inst.status === 'active' ? (inst.unreadCount || 0) : 0), 0);
              return { ...item, collab_instances: updatedInstances, notificationCount: totalUnread, unread: totalUnread > 0 };
            }),
          })));
        }}
        isLoadingCollaborations={isLoadingCollaborations}
        collaborationError={collaborationError}
        subscribeToGroup={subscribeToGroup}
        initialDraft={draftsRef.current.get(selectedItem.collaborationId ?? '')}
        onDraftChange={(draft: { html: string; files: File[] }) => handleDraftChange(selectedItem.collaborationId ?? '', draft)}
      />
    );
  };

  if (error) return <div className="error">Error: {error}</div>;

  // Show loading state while fetching data or auto-selecting
  const isLoading = isLoadingCollaborations || isAutoSelecting;

  const ActiveLeftPanel = LeftPanel;
  const ActiveChatPanel = ChatPanel;

  return (
    <div style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {logoUrl && (
        <div
          className="logoHeader"
          style={{
            height: '60px',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '20px',
            paddingRight: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <img
            src={logoUrl}
            alt="Logo"
            style={{
              height: '50px',
              objectFit: 'contain',
            }}
          />
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {isLoading ? (
            // Loading state
            <div role="status" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              backgroundColor: '#f5f5f5'
            }}>
              <div style={{
                textAlign: 'center',
                padding: '40px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #e0e0e0',
                  borderTop: '4px solid #0857c3',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 20px'
                }} />
                <p style={{
                  color: '#555',
                  fontSize: '16px',
                  fontFamily: "var(--app-font-family, 'Proxima Nova')",
                  margin: 0
                }}>
                  {isLoadingCollaborations ? 'Loading collaborations...' : 'Selecting chat...'}
                </p>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            </div>
          ) : (
            <Allotment>
              <Allotment.Pane preferredSize={350} minSize={150}>
                <ActiveLeftPanel
                  selectedItem={selectedItem}
                  MessagesArray={filteredLeftPanelData}
                  onSelectItem={handleSelectItem}
                  onGroupCreated={(g: MessageItem) => {
                    setIsComposingDM(false);
                    setSelectedItem(g as any);
                    fetchCollaborations();
                  }}
                  onStartNewDM={() => {
                    setSelectedItem(null);
                    setSelectedInstance(null);
                    setIsComposingGroup(false);
                    setIsComposingDM(true);
                  }}
                  onStartNewGroup={() => {
                    setSelectedItem(null);
                    setSelectedInstance(null);
                    setIsComposingDM(false);
                    setIsComposingGroup(true);
                  }}
                  onInstanceSelect={(instance) => setSelectedInstance(instance)}
                  onInstanceCreated={(newInstance, parentItem, sectionName) => {
                    // Add the new instance only if the WebSocket hasn't already added it
                    setLeftPanelData(prevData => {
                      return prevData.map(section => {
                        if (section.name !== sectionName) return section;
                        return {
                          ...section,
                          items: section.items.map((item: any) => {
                            const itemSummaryId = item.collaborationIdSummary || item.collaborationId;
                            const parentSummaryId = parentItem.collaborationIdSummary || parentItem.collaborationId;
                            if (itemSummaryId !== parentSummaryId) return item;
                            const alreadyExists = (item.collab_instances || []).some(
                              (inst: any) => inst.id === newInstance.id
                            );
                            if (alreadyExists) return item;
                            return {
                              ...item,
                              collab_instances: [newInstance, ...(item.collab_instances || [])]
                            };
                          })
                        };
                      });
                    });

                    // Set the selected item to the new instance
                    const instanceItem = {
                      ...parentItem,
                      collaborationId: newInstance.id,
                      chatType: sectionName === "Groups" ? "group" : sectionName === "Projects" ? "project" : "direct"
                    };
                    setSelectedItem(instanceItem as any);
                    setSelectedInstance(newInstance);

                    // WebSocket will confirm the change via "instance_created" event
                  }}
                  onInstanceRenamed={(instance, newTopic, parentItem, sectionName) => {
                    // Optimistic update: update the instance topic in the UI
                    setLeftPanelData(prevData => {
                      return prevData.map(section => {
                        if (section.name !== sectionName) return section;
                        return {
                          ...section,
                          items: section.items.map((item: any) => {
                            const itemSummaryId = item.collaborationIdSummary || item.collaborationId;
                            const parentSummaryId = parentItem.collaborationIdSummary || parentItem.collaborationId;
                            if (itemSummaryId !== parentSummaryId) return item;
                            return {
                              ...item,
                              collab_instances: (item.collab_instances || []).map((inst: CollaborationInstance) =>
                                inst.id === instance.id ? { ...inst, topic: newTopic } : inst
                              )
                            };
                          })
                        };
                      });
                    });

                    // Update selected instance if it was renamed
                    if (selectedInstance?.id === instance.id) {
                      setSelectedInstance({ ...instance, topic: newTopic });
                    }

                    // WebSocket will confirm the change via "topic_updated" event
                  }}
                  onInstanceDeleted={(instanceId, parentItem, sectionName) => {
                    // Optimistic update: remove the instance from the UI
                    setLeftPanelData(prevData => {
                      return prevData.map(section => {
                        if (section.name !== sectionName) return section;
                        return {
                          ...section,
                          items: section.items.map((item: any) => {
                            const itemSummaryId = item.collaborationIdSummary || item.collaborationId;
                            const parentSummaryId = parentItem.collaborationIdSummary || parentItem.collaborationId;
                            if (itemSummaryId !== parentSummaryId) return item;
                            const remaining = (item.collab_instances || []).filter(
                              (inst: CollaborationInstance) => inst.id !== instanceId
                            );
                            const newCount = remaining.reduce((sum: number, inst: any) => sum + (inst.status === 'active' ? (inst.unreadCount || 0) : 0), 0);
                            return {
                              ...item,
                              collab_instances: remaining,
                              notificationCount: newCount,
                              unread: newCount > 0,
                            };
                          })
                        };
                      });
                    });

                    // Clear selected instance and item if the deleted one was active
                    if (selectedInstance?.id === instanceId) {
                      setSelectedInstance(null);
                      setSelectedItem(null);
                    }

                    // WebSocket will confirm the change via "instance_deleted" event
                  }}
                  hideEmptySections={!showLeftPanel}
                  assistantOnly={!showLeftPanel}
                  currentUser={currentUser}
                  allUsersMap={allUsersMap}
                  isLoading={isLoadingCollaborations}
                  draftGroupIds={draftGroupIds}
                  onMentionCleared={(collaborationId, collaborationIdSummary) => {
                    setLeftPanelData(prevData =>
                      prevData.map((section: any) => ({
                        ...section,
                        items: section.items.map((item: any) =>
                          item.collaborationId === collaborationId ||
                          item.collaborationIdSummary === collaborationId ||
                          (collaborationIdSummary && (
                            item.collaborationId === collaborationIdSummary ||
                            item.collaborationIdSummary === collaborationIdSummary
                          ))
                            ? { ...item, hasMention: false }
                            : item
                        )
                      }))
                    );
                  }}
                />
              </Allotment.Pane>
              <Allotment.Pane>
                <Allotment>
                  <Allotment.Pane>{renderRightPanel()}</Allotment.Pane>
                  <Allotment.Pane preferredSize={480} minSize={300} visible={canvas.isOpen} snap>
                    <CanvasPanel document={canvas.document} onClose={canvas.hide} />
                  </Allotment.Pane>
                </Allotment>
              </Allotment.Pane>
            </Allotment>
          )}
        </div>
      </div>
    </div>
  );
}
